import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { useCompanySettings } from '../../hooks/useCompanyData';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { applyAccentColor } from '../../context/ThemeContext';
import { buildLogoUrl } from '../../utils/logo';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import AccountResetSection from './AccountResetSection';
import { Upload, RotateCcw, Trash2, Image } from 'lucide-react';

const DEFAULT_ACCENT = '#2d6a4f';
const DEFAULT_LOGO_SIZE = 36;
const LOGO_SIZE_MIN = 24;
const LOGO_SIZE_MAX = 72;
const WARN_MIN = 30;
const WARN_MAX = 1440;
const ALLOWED_LOGO_TYPES = new Set(['image/svg+xml', 'image/png']);
const MAX_LOGO_BYTES = 1024 * 1024;
const LOGO_UPLOAD_COOLDOWN_MS = 15 * 1000;

const logoStoragePath = (companyId) => `companies/${companyId}/logo/logo`;

function getLogoContentType(file) {
  if (ALLOWED_LOGO_TYPES.has(file.type)) return file.type;

  const lowerName = (file.name || '').toLowerCase();
  if (lowerName.endsWith('.svg')) return 'image/svg+xml';
  if (lowerName.endsWith('.png')) return 'image/png';

  return null;
}

function getLogoUploadTimeMs(settings) {
  return settings?.logoUploadedAt?.toMillis?.() ?? null;
}

async function hasValidPngSignature(file) {
  const signature = [137, 80, 78, 71, 13, 10, 26, 10];
  const bytes = new Uint8Array(await file.slice(0, signature.length).arrayBuffer());
  return signature.every((value, index) => bytes[index] === value);
}

async function hasSafeSvgContent(file) {
  const svgText = await file.text();
  const normalized = svgText.replace(/^\uFEFF/, '').trim();

  if (!/<svg[\s>]/i.test(normalized)) return false;

  const blockedPatterns = [
    /<script\b/i,
    /<foreignObject\b/i,
    /\bon[a-z]+\s*=/i,
    /(?:href|xlink:href)\s*=\s*["']\s*javascript:/i,
  ];

  return !blockedPatterns.some((pattern) => pattern.test(normalized));
}

async function validateLogoFile(file) {
  const contentType = getLogoContentType(file);
  if (!contentType) return { ok: false, reason: 'type' };
  if (!file.size || file.size > MAX_LOGO_BYTES) return { ok: false, reason: 'size' };

  if (contentType === 'image/png') {
    return (await hasValidPngSignature(file))
      ? { ok: true, contentType }
      : { ok: false, reason: 'content' };
  }

  if (contentType === 'image/svg+xml') {
    return (await hasSafeSvgContent(file))
      ? { ok: true, contentType }
      : { ok: false, reason: 'content' };
  }

  return { ok: false, reason: 'type' };
}

function normalize(s) {
  return {
    companyName: s.companyName || '',
    defaultLanguage: s.defaultLanguage || 'en',
    themeDefault: s.themeDefault || 'light',
    activeTaskWarningMinutes: s.activeTaskWarningMinutes ?? 240,
    accentColor: s.accentColor || DEFAULT_ACCENT,
    logoSize: s.logoSize ?? DEFAULT_LOGO_SIZE,
  };
}

export default function AdminSettingsPage() {
  const { t, i18n } = useTranslation();
  const { settings, loading } = useCompanySettings();
  const { companyId } = useAuth();
  const toast = useToast();
  const fileRef = useRef();
  const savedRef = useRef(null);

  const [form, setForm] = useState(normalize({}));
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoRemoving, setLogoRemoving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoVersion, setLogoVersion] = useState(null);
  const [logoUploadedAtMs, setLogoUploadedAtMs] = useState(null);
  const [confirmRemoveLogo, setConfirmRemoveLogo] = useState(false);

  useEffect(() => {
    if (settings) {
      const n = normalize(settings);
      setForm(n);
      savedRef.current = n;
      setLogoUrl(settings.logoUrl || null);
      setLogoVersion(settings.logoVersion || null);
      setLogoUploadedAtMs(getLogoUploadTimeMs(settings));
    }
  }, [settings]);

  const logoSrc = buildLogoUrl(logoUrl, logoVersion);

  const isDirty = savedRef.current && (
    form.companyName.trim() !== savedRef.current.companyName ||
    form.defaultLanguage !== savedRef.current.defaultLanguage ||
    form.themeDefault !== savedRef.current.themeDefault ||
    Number(form.activeTaskWarningMinutes) !== Number(savedRef.current.activeTaskWarningMinutes) ||
    form.accentColor !== savedRef.current.accentColor ||
    form.logoSize !== savedRef.current.logoSize
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleColorChange = (hex) => {
    set('accentColor', hex);
    applyAccentColor(hex);
  };

  const handleResetColor = () => {
    set('accentColor', DEFAULT_ACCENT);
    applyAccentColor(DEFAULT_ACCENT);
  };

  const handleDiscard = () => {
    if (!savedRef.current) return;
    setForm(savedRef.current);
    applyAccentColor(savedRef.current.accentColor || DEFAULT_ACCENT);
  };

  const handleSave = async () => {
    if (!form.companyName.trim()) {
      toast(t('settings.companyNameRequired'), 'error');
      return;
    }
    const warnMins = Number(form.activeTaskWarningMinutes);
    if (!Number.isFinite(warnMins) || warnMins < WARN_MIN || warnMins > WARN_MAX) {
      toast(t('settings.warningMinutesError'), 'error');
      return;
    }

    setSaving(true);
    try {
      const trimmedName = form.companyName.trim();
      await updateDoc(doc(db, COLLECTIONS.SETTINGS, companyId), {
        ...form,
        companyName: trimmedName,
        activeTaskWarningMinutes: warnMins,
        logoSize: Number(form.logoSize),
        updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, COLLECTIONS.COMPANIES, companyId), {
        name: trimmedName,
        defaultLanguage: form.defaultLanguage,
        themeDefault: form.themeDefault,
        updatedAt: serverTimestamp(),
      });
      await i18n.changeLanguage(form.defaultLanguage);
      savedRef.current = { ...form, companyName: trimmedName, activeTaskWarningMinutes: warnMins };
      toast(t('settings.saved'), 'success');
    } catch {
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const lastUploadMs = logoUploadedAtMs || getLogoUploadTimeMs(settings);
    if (lastUploadMs) {
      const msRemaining = LOGO_UPLOAD_COOLDOWN_MS - (Date.now() - lastUploadMs);
      if (msRemaining > 0) {
        toast(t('toast.logoUploadRateLimited', {
          seconds: Math.ceil(msRemaining / 1000),
        }), 'error');
        e.target.value = '';
        return;
      }
    }

    let validation;
    try {
      validation = await validateLogoFile(file);
    } catch {
      toast(t('toast.uploadFailed'), 'error');
      e.target.value = '';
      return;
    }

    if (!validation.ok) {
      const errorKey = validation.reason === 'size'
        ? 'toast.logoTooLarge'
        : validation.reason === 'content'
          ? 'toast.invalidLogoFile'
          : 'toast.svgPngOnly';
      toast(t(errorKey, { sizeMb: 1 }), 'error');
      e.target.value = '';
      return;
    }

    const { contentType } = validation;
    setLogoUploading(true);
    try {
      const storageRef = ref(storage, logoStoragePath(companyId));
      const nextLogoVersion = Date.now();
      await uploadBytes(storageRef, file, {
        contentType,
        cacheControl: 'public,max-age=3600',
      });
      const url = await getDownloadURL(storageRef);
      const logoData = {
        logoPath: storageRef.fullPath,
        logoUrl: url,
        logoVersion: nextLogoVersion,
        logoUploadedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      await Promise.all([
        updateDoc(doc(db, COLLECTIONS.SETTINGS, companyId), logoData),
        updateDoc(doc(db, COLLECTIONS.COMPANIES, companyId), logoData),
      ]);
      setLogoUrl(url);
      setLogoVersion(nextLogoVersion);
      setLogoUploadedAtMs(Date.now());
      toast(t('toast.logoUpdated'), 'success');
    } catch (error) {
      console.error('[Floora] Logo upload failed', {
        companyId,
        code: error?.code,
        message: error?.message,
      });
      toast(t('toast.uploadFailed'), 'error');
    } finally {
      setLogoUploading(false);
      e.target.value = '';
    }
  };

  const handleLogoRemove = async () => {
    setConfirmRemoveLogo(false);
    setLogoRemoving(true);
    try {
      const currentLogoPath = settings?.logoPath || logoStoragePath(companyId);
      try { await deleteObject(ref(storage, currentLogoPath)); } catch { /* not found */ }
      await Promise.all([
        updateDoc(doc(db, COLLECTIONS.SETTINGS, companyId), {
          logoPath: deleteField(),
          logoUrl: deleteField(),
          logoVersion: deleteField(),
          updatedAt: serverTimestamp(),
        }),
        updateDoc(doc(db, COLLECTIONS.COMPANIES, companyId), {
          logoPath: deleteField(),
          logoUrl: deleteField(),
          logoVersion: deleteField(),
          updatedAt: serverTimestamp(),
        }),
      ]);
      setLogoUrl(null);
      setLogoVersion(null);
      toast(t('toast.logoRemoved'), 'success');
    } catch (error) {
      console.error('[Floora] Logo remove failed', {
        companyId,
        code: error?.code,
        message: error?.message,
      });
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setLogoRemoving(false);
    }
  };

  if (loading) return <div className="page-loading"><div className="spinner spinner-lg" /></div>;

  return (
    <div>
      {confirmRemoveLogo && (
        <ConfirmDialog
          title={t('settings.confirmRemoveLogo')}
          message={t('settings.confirmRemoveLogoMessage')}
          confirmLabel={t('settings.removeLogo')}
          confirmClass="btn-danger"
          loading={logoRemoving}
          onConfirm={handleLogoRemove}
          onCancel={() => setConfirmRemoveLogo(false)}
        />
      )}

      <div className="page-header">
        <h1>{t('admin.companySettings')}</h1>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {isDirty && (
            <button className="btn btn-secondary" onClick={handleDiscard} disabled={saving}>
              {t('settings.discardChanges')}
            </button>
          )}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <span className="spinner" /> : t('common.save')}
          </button>
        </div>
      </div>

      <div className="page-content" style={{ maxWidth: 720 }}>

        {/* ── Company & Branding ── */}
        <div className="card">
          <div className="card-header"><span className="card-title">{t('settings.companyBranding')}</span></div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <div className="form-group">
              <label className="form-label">{t('settings.companyName')}</label>
              <input
                className="form-control"
                value={form.companyName}
                onChange={(e) => set('companyName', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('settings.companyLogo')}</label>

              <div style={styles.logoPreviewBox}>
                {logoSrc ? (
                  <img
                    src={logoSrc}
                    alt={t('settings.logoPreviewAlt')}
                    style={{ height: Number(form.logoSize), maxWidth: '100%', objectFit: 'contain', display: 'block' }}
                  />
                ) : (
                  <div style={styles.logoPlaceholder}>
                    <Image size={24} style={{ color: 'var(--text-muted)', marginBottom: 6 }} />
                    <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                      {t('settings.noLogo')}
                    </span>
                  </div>
                )}
              </div>

              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-3)', marginTop: 4 }}>
                {t('settings.logoFormats')}
              </p>

              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <input
                  type="file"
                  accept="image/svg+xml,image/png"
                  ref={fileRef}
                  style={{ display: 'none' }}
                  onChange={handleLogoUpload}
                />
                <button
                  className="btn btn-secondary"
                  onClick={() => fileRef.current?.click()}
                  disabled={logoUploading || logoRemoving}
                >
                  {logoUploading ? <span className="spinner" /> : (
                    <><Upload size={15} />{logoUrl ? t('settings.replaceLogo') : t('settings.uploadLogo')}</>
                  )}
                </button>

                {logoUrl && (
                  <button
                    className="btn btn-ghost"
                    onClick={() => setConfirmRemoveLogo(true)}
                    disabled={logoUploading || logoRemoving}
                    style={{ color: 'var(--color-danger)' }}
                  >
                    {logoRemoving ? <span className="spinner" /> : <><Trash2 size={15} />{t('settings.removeLogo')}</>}
                  </button>
                )}
              </div>

              {logoUrl && (
                <div style={{ marginTop: 'var(--space-4)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                    <label className="form-label" style={{ marginBottom: 0 }}>{t('settings.logoSize')}</label>
                    <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                      {form.logoSize}px
                    </span>
                  </div>
                  <input
                    type="range"
                    min={LOGO_SIZE_MIN}
                    max={LOGO_SIZE_MAX}
                    step={2}
                    value={form.logoSize}
                    onChange={(e) => set('logoSize', Number(e.target.value))}
                    style={{ width: '100%', accentColor: 'var(--color-brand)' }}
                  />
                  <p className="form-hint">{t('settings.logoSizeHint')}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Appearance ── */}
        <div className="card">
          <div className="card-header"><span className="card-title">{t('settings.appearance')}</span></div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div className="form-group">
              <label className="form-label">{t('settings.defaultTheme')}</label>
              <select className="form-control" value={form.themeDefault} onChange={(e) => set('themeDefault', e.target.value)}>
                <option value="light">{t('settings.light')}</option>
                <option value="dark">{t('settings.dark')}</option>
                <option value="system">{t('settings.system')}</option>
              </select>
              <p className="form-hint">{t('settings.defaultThemeHint')}</p>
            </div>

            <div className="form-group">
              <label className="form-label">{t('settings.accentColor')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                <div style={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }}>
                  <input
                    type="color"
                    value={form.accentColor}
                    onChange={(e) => handleColorChange(e.target.value)}
                    style={{
                      width: '100%',
                      height: '100%',
                      padding: 2,
                      borderRadius: 'var(--radius)',
                      border: '1.5px solid var(--border-color)',
                      cursor: 'pointer',
                      background: 'var(--surface-1)',
                    }}
                  />
                </div>
                <span style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
                  {form.accentColor}
                </span>
                <button
                  className="btn btn-ghost"
                  onClick={handleResetColor}
                  title={t('settings.resetColor')}
                  style={{ padding: '6px 10px' }}
                >
                  <RotateCcw size={14} />
                  {t('settings.resetColor')}
                </button>
              </div>
              <p className="form-hint">{t('settings.accentColorHint')}</p>
            </div>

            <div className="form-group">
              <label className="form-label">{t('settings.defaultLanguage')}</label>
              <select className="form-control" value={form.defaultLanguage} onChange={(e) => set('defaultLanguage', e.target.value)}>
                <option value="en">{t('languages.en')}</option>
                <option value="vi">{t('languages.vi')}</option>
              </select>
              <p className="form-hint">{t('settings.defaultLanguageHint')}</p>
            </div>
          </div>
        </div>

        {/* ── Production Settings ── */}
        <div className="card">
          <div className="card-header"><span className="card-title">{t('settings.productionSettings')}</span></div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div className="form-group">
              <label className="form-label">{t('settings.activeTaskWarning')}</label>
              <input
                type="number"
                min={WARN_MIN}
                max={WARN_MAX}
                className="form-control"
                value={form.activeTaskWarningMinutes}
                onChange={(e) => set('activeTaskWarningMinutes', e.target.value)}
              />
              <p className="form-hint">{t('settings.activeTaskWarningHint')}</p>
            </div>
            <p className="form-hint">{t('settings.labourRatesNote')}</p>
          </div>
        </div>

        {/* ── Full Account Data Reset (Danger Zone) ── */}
        <AccountResetSection />

        {/* Bottom save bar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)', paddingBottom: 'var(--space-6)' }}>
          {isDirty && (
            <button className="btn btn-secondary" onClick={handleDiscard} disabled={saving}>
              {t('settings.discardChanges')}
            </button>
          )}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <span className="spinner" /> : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  logoPreviewBox: {
    border: '1.5px dashed var(--border-color)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--surface-2)',
    minHeight: 96,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-4)',
    marginBottom: 'var(--space-2)',
  },
  logoPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
};
